import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, MenuItem, Stack, TextField, Typography} from 'views/components';
import {ExpandMore} from 'views/icons';
import {Translate, translate} from 'views/module';

const TEAM_OPTIONS = [
  {
    value: 'sales',
    labelExpression: 'domainGuide.Detail.TeamOptions.Sales',
  },
  {
    value: 'recruiting',
    labelExpression: 'domainGuide.Detail.TeamOptions.Recruiting',
  },
  {
    value: 'operations',
    labelExpression: 'domainGuide.Detail.TeamOptions.Operations',
  },
];

const SLACK_ID_OPTIONS = [
  {
    value: 'U069D53HQ4R',
    labelExpression: 'domainGuide.Detail.SlackIdOptions.Blake',
  },
  {
    value: 'U0A6P1A0TNJ',
    labelExpression: 'domainGuide.Detail.SlackIdOptions.Bianca',
  },
  {
    value: 'U0AFY9EDX34',
    labelExpression: 'domainGuide.Detail.SlackIdOptions.Daniel',
  },
  {
    value: 'U09G8K7SWUF',
    labelExpression: 'domainGuide.Detail.SlackIdOptions.Patrick',
  },
];

const DomainGuideAssignment = (props) => {
  const {
    domainGuideData,
    updateSlackIds,
    updateTeamName,
  } = props;

  const [selectedTeamName, setSelectedTeamName] = React.useState(domainGuideData.teamName || '');
  const slackIds = Array.isArray(domainGuideData.slackIds) ?
    domainGuideData.slackIds.filter(Boolean) :
    [];

  React.useEffect(() => {
    setSelectedTeamName(domainGuideData.teamName || '');
  }, [domainGuideData.teamName]);

  const getOptionLabel = (options, value) => {
    const option = options.find((currentOption) => currentOption.value === value);

    return option ? translate(option.labelExpression) : value;
  };

  const handleTeamNameChange = (teamName) => {
    if (!teamName || teamName === selectedTeamName) {
      return;
    }

    setSelectedTeamName(teamName);
    updateTeamName(teamName);
  };

  const handleSlackIdToggle = (slackId) => {
    if (!slackId) {
      return;
    }

    const nextSlackIds = slackIds.includes(slackId) ?
      slackIds.filter((currentSlackId) => currentSlackId !== slackId) :
      [...new Set([...slackIds, slackId])];

    updateSlackIds(nextSlackIds);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E4DE',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{gap: 2, px: 3, py: 2}}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '11px',
            lineHeight: '14px',
            color: '#A8A49E',
          }}
        >
          <Translate expression='domainGuide.Detail.Assignment.Title' />
        </Typography>
        <Stack sx={{gap: '8px', minWidth: 0}}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
            }}
          >
            <Translate expression='domainGuide.Detail.Fields.TeamName' />
          </Typography>
          <TextField
            select
            fullWidth
            value={selectedTeamName}
            onChange={handleTeamNameChange}
            SelectProps={{
              displayEmpty: true,
              IconComponent: ExpandMore,
              renderValue: (value) => {
                return value ?
                  getOptionLabel(TEAM_OPTIONS, value) :
                  translate('domainGuide.Detail.Assignment.TeamPlaceholder');
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
              },
              '& .MuiSelect-select': {
                px: '16px',
                py: '12px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '150%',
                color: selectedTeamName ? '#1A1916' : '#A8A49E',
                boxSizing: 'border-box',
              },
              '& .MuiSelect-icon': {
                color: '#1A1916',
                right: '12px',
              },
            }}
          >
            <MenuItem value='' disabled>
              <Translate expression='domainGuide.Detail.Assignment.TeamPlaceholder' />
            </MenuItem>
            {
              TEAM_OPTIONS.map((teamOption) => (
                <MenuItem
                  key={teamOption.value}
                  value={teamOption.value}
                >
                  <Translate expression={teamOption.labelExpression} />
                </MenuItem>
              ))
            }
          </TextField>
        </Stack>
        <Stack sx={{gap: '8px', minWidth: 0}}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
            }}
          >
            <Translate expression='domainGuide.Detail.Assignment.SlackIdLabel' />
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {
              SLACK_ID_OPTIONS.map((slackIdOption) => {
                const isSelected = slackIds.includes(slackIdOption.value);

                return (
                  <Button
                    key={slackIdOption.value}
                    aria-pressed={isSelected}
                    onClick={() => handleSlackIdToggle(slackIdOption.value)}
                    sx={{
                      'minHeight': '32px',
                      'px': '12px',
                      'py': '6px',
                      'border': '1px solid',
                      'borderColor': isSelected ? '#DA7955' : '#E8E4DE',
                      'borderRadius': '999px',
                      'backgroundColor': isSelected ? '#FFF4EE' : '#FFFFFF',
                      'boxShadow': 'none',
                      'color': isSelected ? '#C4512C' : '#1A1916',
                      'fontSize': '12px',
                      'fontWeight': 700,
                      'lineHeight': '16px',
                      'textTransform': 'none',
                      '&:hover': {
                        borderColor: isSelected ? '#C4512C' : '#D8D2CA',
                        backgroundColor: isSelected ? '#FFF1EA' : '#F8F6F3',
                        boxShadow: 'none',
                      },
                    }}
                    variant='outlined'
                  >
                    <Translate expression={slackIdOption.labelExpression} />
                  </Button>
                );
              })
            }
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

DomainGuideAssignment.propTypes = {
  domainGuideData: PropTypes.object.isRequired,
  updateSlackIds: PropTypes.func.isRequired,
  updateTeamName: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideAssignment);
